// PAGINACIÓN DE LISTADOS (catálogo; operador, 5-oct-2026: «los listados paginan de 10 en 10»).
//
// El mismo pie que la Bandeja de casos: «Mostrando 1 a 10 de 48 casos.» y, si hay más de una
// página, «← Anterior · Página 1 de 5 · Siguiente →» en un `nav` con nombre. Sirve igual para
// la paginación del SERVIDOR (la pantalla pasa `total` y las filas de esta página) que para la
// del CLIENTE (`paginar` trocea la lista ya traída). Sin rayas en el texto (lineamiento).
import type { ReactNode } from 'react';
import { Button, Mono } from '../ui';

/** Filas por página de todo listado (decisión del operador, 5-oct-2026). */
export const POR_PAGINA_DE_LISTADOS = 10;

/** Paginación en el cliente: las filas de la página `pagina` (empieza en 1). */
export function paginar<T>(filas: readonly T[], pagina: number, porPagina = POR_PAGINA_DE_LISTADOS): T[] {
  const inicio = (Math.max(1, pagina) - 1) * porPagina;
  return filas.slice(inicio, inicio + porPagina);
}

/** Cuántas páginas hay (al menos una, para que «Página 1 de 1» tenga sentido). */
export const paginasDe = (total: number, porPagina = POR_PAGINA_DE_LISTADOS): number =>
  Math.max(1, Math.ceil(total / porPagina));

export type PropsDePaginacion = {
  pagina: number;
  total: number;
  /** Cuántas filas trae ESTA página (la última puede traer menos). */
  enEstaPagina: number;
  porPagina?: number;
  onPagina: (pagina: number) => void;
  /** El nombre accesible del `nav`, p. ej. «Paginación de los compromisos». */
  etiqueta: string;
  /** Singular y plural de lo que se lista: ['caso', 'casos']. */
  sustantivo?: [string, string];
  /** Texto extra tras el contador (p. ej. «Consultada el …»). */
  children?: ReactNode;
  /** Base de los testid: `-contador`, `-anterior`, `-siguiente`. */
  'data-testid'?: string;
};

export default function Paginacion({
  pagina, total, enEstaPagina, porPagina = POR_PAGINA_DE_LISTADOS, onPagina, etiqueta,
  sustantivo = ['registro', 'registros'], children, 'data-testid': testid,
}: PropsDePaginacion) {
  const paginas = paginasDe(total, porPagina);
  const primero = enEstaPagina ? (pagina - 1) * porPagina + 1 : 0;
  const ultimo = (pagina - 1) * porPagina + enEstaPagina;
  const t = (sufijo: string) => (testid ? `${testid}-${sufijo}` : undefined);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={testid}>
      <p className="text-meta text-ink-soft" role="status" data-testid={t('contador')}>
        Mostrando <Mono className="text-ink">{primero ? `${primero} a ${ultimo}` : '0'}</Mono> de{' '}
        <Mono className="text-ink">{total}</Mono> {total === 1 ? sustantivo[0] : sustantivo[1]}.
        {children}
      </p>
      {paginas > 1 && (
        <nav className="flex items-center gap-2" aria-label={etiqueta}>
          <Button size="sm" variant="secondary" disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)} data-testid={t('anterior')}>
            ← Anterior
          </Button>
          <span className="text-meta text-ink-soft whitespace-nowrap">
            Página <Mono className="text-ink">{pagina}</Mono> de <Mono className="text-ink">{paginas}</Mono>
          </span>
          <Button size="sm" variant="secondary" disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)} data-testid={t('siguiente')}>
            Siguiente →
          </Button>
        </nav>
      )}
    </div>
  );
}
